/**
 * Shared store of CookUp Core custom wavetables, keyed by content.
 *
 * Tables are large (up to 256 × 2048 floats), so they never live inside the
 * patch object, which is cloned on every knob move. The patch keeps a small
 * reference ({ key, name, frames, storageKey }); the cycles live here, shared
 * by every engine and track that uses the same table. A table imported on
 * another device is fetched from storage by its storageKey on first use.
 */
import factoryTables from './banks/signatureSynthTables.json';
import { engineAssetUrl } from '../audio/dspAssetUrl';
import { resolveInstrumentSampleUrl } from './sampleAssetUrl';
import { cyclesFromWav, parseWav } from './wavetableImport';

const tables = new Map();
const loads = new Map();
const compiled = new Map();
const compiledLoads = new Map();
export const factoryCoreTable = reference => factoryTables.find(table => table.key === reference?.key);

export function registerCoreTable(key, cycles) {
  if (key && cycles instanceof Float32Array && cycles.length > 0) tables.set(key, cycles);
}

export function getCoreTable(key) {
  return key ? tables.get(key) || null : null;
}

/** Cycles for a patch's table reference, fetching them from storage if needed. */
export function loadCoreTable(reference) {
  const key = reference?.key;
  if (!key) return Promise.resolve(null);
  if (tables.has(key)) return Promise.resolve(tables.get(key));
  const chatUrl = typeof window !== 'undefined' && window.__COOKUP_CHAT_PLAYER__ ? window.__COOKUP_CHAT_TABLES__?.[key] : null;
  const factory = factoryCoreTable(reference);
  if ((!reference.storageKey && !chatUrl && !factory) || typeof fetch !== 'function') return Promise.resolve(null);
  if (loads.has(key)) return loads.get(key);
  const load = (async () => {
    try {
      const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;
      const url = factory ? engineAssetUrl(factory.wav) : chatUrl || await resolveInstrumentSampleUrl({ storageKey: reference.storageKey }, token);
      if (!url) return null;
      const response = await fetch(url);
      if (!response.ok) return null;
      const parsed = parseWav(await response.arrayBuffer());
      if (!parsed) return null;
      const { cycles } = cyclesFromWav(parsed);
      tables.set(key, cycles);
      return cycles;
    } catch (_) {
      return null;
    } finally {
      loads.delete(key);
    }
  })();
  loads.set(key, load);
  return load;
}

/** Immutable build-time mips: no FFT in the playback worklet for factory tables. */
export function loadCompiledCoreTable(reference) {
  const factory = factoryCoreTable(reference);
  if (!factory) return Promise.resolve(null);
  if (compiled.has(factory.key)) return Promise.resolve(compiled.get(factory.key));
  if (compiledLoads.has(factory.key)) return compiledLoads.get(factory.key);
  const task = (async () => {
    try {
      const response = await fetch(engineAssetUrl(factory.compiled));
      if (!response.ok) throw new Error(`Could not load ${factory.name}.`);
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength !== factory.bytes) throw new Error(`Incomplete wavetable: ${factory.name}.`);
      const values = new Float32Array(buffer);
      if (values[0] !== 17237 || values[1] !== 1 || values[2] !== factory.frames) throw new Error(`Invalid wavetable: ${factory.name}.`);
      compiled.set(factory.key, values);
      return values;
    } finally { compiledLoads.delete(factory.key); }
  })();
  compiledLoads.set(factory.key, task);
  return task;
}
